import styled from '@emotion/styled';
import { LoadingButton } from '@mui/lab';
import { DialogTitle, InputLabel } from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import useCodingProfileEdit from 'api/codingProfile/useCodingProfileEdit/useCodingProfileEdit';
import useCodingProfileType from 'api/codingProfile/useCodingProfileType/useCodingProfileType';
import useGetAllCodingProfileDecoder from 'api/codingProfile/useGetAllCodingProfileDecoder/useGetAllCodingProfileDecoder';
import useGetCodingProfileByGuid from 'api/codingProfile/useGetCodingProfileByGuid/useGetCodingProfileByGuid';
import useGetAllProfileLogic from 'api/productSetting/useGetAllProfileLogic';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import SpinnerLoader from '../../../SpinnerLoader/SpinnerLoader';

// eslint-disable-next-line complexity
const CodingProfileEditModal = ({
  open,
  handleClose,
  chosenCode,
  getCodingProfileList,
}) => {
  const [codeName, setCodeName] = useState('');
  const [prefix, setPrefix] = useState('');
  const [codeType, setCodeType] = useState('');
  const [codeLogic, setCodeLogic] = useState('');
  // 1 => title required, 0 => title not required
  const [isTitleRequired, setIsTitleRequired] = useState('0');
  const [isStringLengthControlled, setIsStringLengthControlled] = useState('0');
  const [stringLength, setStringLength] = useState('');
  const [decoder, setDecoder] = useState('0');
  const [formula, setFormula] = useState('');
  const [skuLength, setSkuLength] = useState('');
  const [serialLength, setSerialLength] = useState('');
  const [codePrefix, setCodePrefix] = useState('');
  const [codePostfix, setCodePostfix] = useState('');
  const [trimStart, setTrimStart] = useState('');
  const [trimEnd, setTrimEnd] = useState('');
  const [emptyError, setEmptyError] = useState(false);

  const [getCodingProfileTypeList, typesLoading, codingProfileTypeList] =
    useCodingProfileType();
  const [getAllProfileLogicList, logicsLoading, allProfileLogicList] =
    useGetAllProfileLogic();
  const {
    decoderList: codingProfileDecoderList,
    loading: decodersLoading,
    getCodingProfileDecoderList,
  } = useGetAllCodingProfileDecoder();
  const { editRequest, loading: editLoading } = useCodingProfileEdit();
  const {
    getCodingProfileByGuid,
    codingProfile,
    loading: codingProfileLoading,
    resetCodingProfile,
  } = useGetCodingProfileByGuid();
  const isSkuSerialDecoder = decoder === '1';
  const displayedStringLength = useMemo(() => {
    if (!isSkuSerialDecoder) return stringLength;

    if (!skuLength && !serialLength) return '';

    const sku = Number(skuLength) || 0;
    const serial = Number(serialLength) || 0;
    return String(sku + serial);
  }, [isSkuSerialDecoder, serialLength, skuLength, stringLength]);
  const isTotalStringLengthInvalid =
    isStringLengthControlled === '1' &&
    (!displayedStringLength.trim() ||
      !Number.isFinite(Number(displayedStringLength)) ||
      Number(displayedStringLength) <= 0 ||
      (isSkuSerialDecoder && !skuLength && !serialLength));

  useEffect(() => {
    getCodingProfileTypeList();
    getCodingProfileDecoderList();
    getAllProfileLogicList();
  }, []);

  useEffect(() => {
    if (open && chosenCode?.id) {
      setCodeType('');
      getCodingProfileByGuid(chosenCode.id);
    }
  }, [open, chosenCode?.id]);

  // eslint-disable-next-line complexity
  useEffect(() => {
    if (!codingProfile) return;
    setCodeName(codingProfile.display ?? '');
    setPrefix(codingProfile.prefix ?? '');
    setCodeType(codingProfile.type ?? '');
    setCodeLogic(codingProfile.logic ?? '');
    setIsTitleRequired(codingProfile.titleRequired ? '1' : '0');
    setIsStringLengthControlled(codingProfile.validationlength ? '1' : '0');
    setStringLength(
      codingProfile.validationlengthValue == null
        ? ''
        : String(codingProfile.validationlengthValue),
    );
    setDecoder(String(codingProfile.decoder ?? 0));
    setSkuLength(
      codingProfile.skUlength == null ? '' : String(codingProfile.skUlength),
    );
    setSerialLength(
      codingProfile.seriallength == null
        ? ''
        : String(codingProfile.seriallength),
    );
    setFormula(codingProfile.formula ?? '');
    setCodePrefix(codingProfile.codePrefix ?? '');
    setCodePostfix(codingProfile.codePostfix ?? '');
    setTrimStart(
      codingProfile.codeTrimStart == null
        ? ''
        : String(codingProfile.codeTrimStart),
    );
    setTrimEnd(
      codingProfile.codeTrimEnd == null
        ? ''
        : String(codingProfile.codeTrimEnd),
    );
  }, [codingProfile]);

  const resetForm = useCallback(() => {
    setCodeName('');
    setPrefix('');
    setCodeType('');
    setCodeLogic('');
    setIsTitleRequired('0');
    setIsStringLengthControlled('0');
    setStringLength('');
    setDecoder('0');
    setFormula('');
    setSkuLength('');
    setSerialLength('');
    setCodePrefix('');
    setCodePostfix('');
    setTrimStart('');
    setTrimEnd('');
    setEmptyError(false);
    resetCodingProfile();
  }, [resetCodingProfile]);

  useEffect(() => {
    if (!open) resetForm();
  }, [open, resetForm]);

  const closeModal = useCallback(() => {
    resetForm();
    handleClose();
  }, [handleClose, resetForm]);

  const submitCodeGroup = () => {
    if (codeName && codeType && codeLogic && !isTotalStringLengthInvalid) {
      setEmptyError(false);
      editRequest({
        guid: codingProfile.guid,
        codeName,
        prefix,
        codeType,
        codeLogic,
        titleRequired: isTitleRequired === '1',
        validationlength: isStringLengthControlled === '1',
        validationlengthValue:
          isStringLengthControlled === '1'
            ? Number(displayedStringLength) || 0
            : 0,
        decoder: Number(decoder),
        skUlength: decoder === '1' ? skuLength : '',
        seriallength: decoder === '1' ? serialLength : '',
        formula: decoder === '2' ? formula : '',
        codePrefix: decoder === '3' ? codePrefix : '',
        codePostfix: decoder === '3' ? codePostfix : '',
        codeTrimStart: decoder === '4' ? trimStart : '',
        codeTrimEnd: decoder === '4' ? trimEnd : '',
        onSuccess: () => {
          closeModal();
          getCodingProfileList();
        },
      });
    } else {
      setEmptyError(true);
    }
  };

  return (
    <Dialog open={open} onClose={closeModal} sx={{ direction: 'rtl' }}>
      <DialogTitle>ویراش کد پرفایل</DialogTitle>

      <DialogContent sx={{ minHeight: 150, minWidth: 300 }}>
        {codingProfileLoading ? (
          <SpinnerLoader />
        ) : codeType ? (
          <>
            <FormFieldWrapper>
              <TextField
                autoFocus
                variant="standard"
                label="عنوان"
                value={codeName}
                onChange={e => setCodeName(e.target.value)}
                sx={{ minWidth: 300 }}
                error={!codeName && emptyError}
              />
            </FormFieldWrapper>

            <FormFieldWrapper>
              <TextField
                autoFocus
                variant="standard"
                label="پیشوند"
                value={prefix}
                onChange={e => setPrefix(e.target.value)}
                sx={{ minWidth: 300 }}
                error={!prefix && emptyError}
              />
            </FormFieldWrapper>

            <FormFieldWrapper>
              <FormControl
                variant="standard"
                sx={{ width: '100%' }}
                error={!codeLogic && emptyError}
              >
                <InputLabel id="edit-codeLogic-label">منطق</InputLabel>
                <Select
                  labelId="edit-codeLogic-label"
                  id="edit-codeLogic"
                  value={codeLogic}
                  onChange={e => setCodeLogic(e.target.value)}
                >
                  {logicsLoading ? (
                    <SpinnerLoader />
                  ) : (
                    allProfileLogicList?.map(type => (
                      <MenuItem
                        value={type.key}
                        key={type.key}
                        sx={{ direction: 'rtl' }}
                      >
                        {type.value}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </FormFieldWrapper>

            <FormFieldWrapper>
              <FormControl
                variant="standard"
                sx={{ width: '100%' }}
                error={!codeType && emptyError}
              >
                <InputLabel id="edit-codeType-label">نوع</InputLabel>
                <Select
                  labelId="edit-codeType-label"
                  id="edit-codeType"
                  label="نوع"
                  value={codeType}
                  onChange={e => setCodeType(e.target.value)}
                >
                  {typesLoading ? (
                    <SpinnerLoader />
                  ) : (
                    codingProfileTypeList.map(type => (
                      <MenuItem
                        value={type.key}
                        key={type.key}
                        sx={{ direction: 'rtl' }}
                      >
                        {type.value}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </FormFieldWrapper>

            {codeType === '1' && (
              <>
                <FormFieldWrapper>
                  <FormControl variant="standard" sx={{ width: '100%' }}>
                    <InputLabel id="edit-titleRequired-label">
                      عنوان اجباری
                    </InputLabel>
                    <Select
                      labelId="edit-titleRequired-label"
                      id="edit-titleRequired"
                      label="عنوان اجباری"
                      value={isTitleRequired}
                      onChange={e => setIsTitleRequired(e.target.value)}
                    >
                      <MenuItem value="1" sx={{ direction: 'rtl' }}>
                        بله
                      </MenuItem>
                      <MenuItem value="0" sx={{ direction: 'rtl' }}>
                        خیر
                      </MenuItem>
                    </Select>
                  </FormControl>
                </FormFieldWrapper>

                <FormFieldWrapper>
                  <FormControl variant="standard" sx={{ width: '100%' }}>
                    <InputLabel id="edit-validationLength-label">
                      کنترل طول رشته
                    </InputLabel>
                    <Select
                      labelId="edit-validationLength-label"
                      id="edit-validationLength"
                      value={isStringLengthControlled}
                      label="کنترل طول رشته"
                      onChange={e =>
                        setIsStringLengthControlled(e.target.value)
                      }
                    >
                      <MenuItem value="1" sx={{ direction: 'rtl' }}>
                        بله
                      </MenuItem>
                      <MenuItem value="0" sx={{ direction: 'rtl' }}>
                        خیر
                      </MenuItem>
                    </Select>
                  </FormControl>
                </FormFieldWrapper>

                {isStringLengthControlled === '1' && (
                  <FormFieldWrapper>
                    <TextField
                      type="number"
                      variant="standard"
                      label="طول رشته"
                      value={displayedStringLength}
                      onChange={e => setStringLength(e.target.value)}
                      disabled={isSkuSerialDecoder}
                      error={emptyError ? isTotalStringLengthInvalid : null}
                      sx={{ minWidth: 300 }}
                    />
                  </FormFieldWrapper>
                )}

                <FormFieldWrapper>
                  <FormControl variant="standard" sx={{ width: '100%' }}>
                    <InputLabel id="edit-decoder-label">دیکودر</InputLabel>
                    <Select
                      labelId="edit-decoder-label"
                      id="edit-decoder"
                      value={decoder}
                      onChange={e => setDecoder(e.target.value)}
                    >
                      {decodersLoading ? (
                        <SpinnerLoader />
                      ) : (
                        codingProfileDecoderList.map(item => (
                          <MenuItem
                            value={item.key}
                            key={item.key}
                            sx={{ direction: 'rtl' }}
                          >
                            {item.value}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  </FormControl>
                </FormFieldWrapper>

                {decoder === '2' && (
                  <FormFieldWrapper>
                    <TextField
                      variant="standard"
                      label="فرمول"
                      value={formula}
                      onChange={e => setFormula(e.target.value)}
                      sx={{ minWidth: 300 }}
                    />
                  </FormFieldWrapper>
                )}

                {decoder === '1' && (
                  <>
                    <FormFieldWrapper>
                      <TextField
                        type="number"
                        variant="standard"
                        label="طول SKU"
                        value={skuLength}
                        onChange={e => setSkuLength(e.target.value)}
                        sx={{ minWidth: 300 }}
                      />
                    </FormFieldWrapper>
                    <FormFieldWrapper>
                      <TextField
                        type="number"
                        variant="standard"
                        label="طول سریال"
                        value={serialLength}
                        onChange={e => setSerialLength(e.target.value)}
                        sx={{ minWidth: 300 }}
                      />
                    </FormFieldWrapper>
                  </>
                )}

                {decoder === '3' && (
                  <>
                    <FormFieldWrapper>
                      <TextField
                        variant="standard"
                        label="Prefix"
                        value={codePrefix}
                        onChange={e => setCodePrefix(e.target.value)}
                        sx={{ minWidth: 300 }}
                      />
                    </FormFieldWrapper>
                    <FormFieldWrapper>
                      <TextField
                        variant="standard"
                        label="Postfix"
                        value={codePostfix}
                        onChange={e => setCodePostfix(e.target.value)}
                        sx={{ minWidth: 300 }}
                      />
                    </FormFieldWrapper>
                  </>
                )}

                {decoder === '4' && (
                  <>
                    <FormFieldWrapper>
                      <TextField
                        type="number"
                        variant="standard"
                        label="شروع"
                        value={trimStart}
                        onChange={e => setTrimStart(e.target.value)}
                        inputProps={{ min: 0, step: 1 }}
                        sx={{ minWidth: 300 }}
                      />
                    </FormFieldWrapper>
                    <FormFieldWrapper>
                      <TextField
                        type="number"
                        variant="standard"
                        label="پایان"
                        value={trimEnd}
                        onChange={e => setTrimEnd(e.target.value)}
                        inputProps={{ min: 0, step: 1 }}
                        sx={{ minWidth: 300 }}
                      />
                    </FormFieldWrapper>
                  </>
                )}
              </>
            )}
          </>
        ) : null}
      </DialogContent>

      <DialogActions sx={{ gap: 1, padding: 3 }}>
        <Button onClick={closeModal} variant="contained" disabled={editLoading}>
          انصراف
        </Button>
        <LoadingButton
          variant="contained"
          color="warning"
          onClick={submitCodeGroup}
          loading={editLoading}
        >
          ویرایش
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default CodingProfileEditModal;

const FormFieldWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 30px;
  margin-top: 30px;
`;
